import { computed, ref, toValue, watch, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import type { ReportPaginationChange } from '../components/table/types'
import type { ReportViewType } from '../models/chart.types'
import type { ReportColumn } from '../models/column.types'
import type {
  DataVisiblePaginationConfig,
  DataVisibleTableMode,
  DataVisibleTrendConfig,
} from '../models/dataVisible.types'
import type { ReportFilter } from '../models/filter.types'
import type { ReportSpec } from '../models/report.types'
import type { ReportTableSortV1 } from '../models/tableConfig.types'
import type { ReportCardStatus } from '../models/tablePresentation.types'
import type { TrendTableFrequency } from '../models/trendTable.types'
import { cardStatusFilters } from '../service/cardStatusFilters'
import { buildTrendReportSpecPatch } from '../service/trendTablePresentation'

// Keep the initial request large enough to overflow the scroll viewport. The configured page size
// remains the increment used by loadMore; it must not shrink the first request back to 10 rows.
const INITIAL_REQUEST_ROWS = 250
const DEFAULT_PAGE_ROWS = 10

export interface ResolvedDataVisibleTrend {
  frequency: TrendTableFrequency
  breakdownColumn: ReportColumn
  previewColumn: ReportColumn
  dateDimensions: ReportColumn[]
}

export interface ResolvedDataVisiblePagination extends ReportPaginationChange {
  pageSizes: number[]
}

export interface UseDataVisibleReportRequestOptions {
  reportId: MaybeRefOrGetter<number>
  partitionName: MaybeRefOrGetter<string | undefined>
  defaultPostData: MaybeRefOrGetter<string | undefined>
  view: MaybeRefOrGetter<ReportViewType>
  viewUnavailable: MaybeRefOrGetter<boolean>
  tableUnavailable: MaybeRefOrGetter<boolean>
  chartCatalogLoading: MaybeRefOrGetter<boolean>
  chartUnavailable: MaybeRefOrGetter<boolean>
  chartFieldsValid: MaybeRefOrGetter<boolean>
  tableDimensions: MaybeRefOrGetter<readonly string[]>
  tableMetrics: MaybeRefOrGetter<readonly string[]>
  chartDimensions: MaybeRefOrGetter<readonly string[]>
  chartTimeDimensions: MaybeRefOrGetter<readonly string[]>
  chartMetrics: MaybeRefOrGetter<readonly string[]>
  chartActive: MaybeRefOrGetter<boolean>
  filters: MaybeRefOrGetter<readonly ReportFilter[]>
  cardStatus?: MaybeRefOrGetter<ReportCardStatus>
  sort: MaybeRefOrGetter<ReportTableSortV1 | null>
  chartLimit: MaybeRefOrGetter<number | null | undefined>
  tableMode: MaybeRefOrGetter<DataVisibleTableMode>
  trend: MaybeRefOrGetter<DataVisibleTrendConfig | undefined>
  breakdownField: MaybeRefOrGetter<string | null>
  columns: MaybeRefOrGetter<readonly ReportColumn[]>
  tablePresenterAvailable: MaybeRefOrGetter<boolean>
  pagination: MaybeRefOrGetter<DataVisiblePaginationConfig | false | undefined>
  partitionOnly?: MaybeRefOrGetter<boolean | undefined>
  countUniqueDimensions?: MaybeRefOrGetter<readonly string[] | undefined>
  edges?: MaybeRefOrGetter<readonly string[] | undefined>
  targetCurrency?: MaybeRefOrGetter<string | null | undefined>
  addCompare?: MaybeRefOrGetter<boolean | undefined>
}

export interface UseDataVisibleReportRequest {
  spec: ComputedRef<ReportSpec | null>
  trend: ComputedRef<ResolvedDataVisibleTrend | null>
  trendUnavailable: ComputedRef<boolean>
  pagination: ComputedRef<ResolvedDataVisiblePagination | null>
  /** Grows the scroll window by one more configured page; a no-op once pagination is unconfigured. */
  loadMore: () => void
}

function nonNegativeInteger(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : fallback
}

function positiveInteger(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : fallback
}

// `false` is the one explicit opt-out (e.g. a report whose presenter needs its full result set in
// one read); an omitted config is not "no pagination" — it means "use the default page size" — so
// every table/card request scrolls by default instead of only the few call sites that configure it.
function paginationConfig(
  value: DataVisiblePaginationConfig | false | undefined,
): ResolvedDataVisiblePagination | null {
  if (value === false) return null
  const config = value ?? {}
  const sizes = [
    ...new Set((config.pageSizes ?? []).map((size) => positiveInteger(size, 0))),
  ].filter((size) => size > 0)
  const rows = positiveInteger(config.rows, sizes[0] ?? DEFAULT_PAGE_ROWS)
  return {
    first: Math.floor(nonNegativeInteger(config.first) / rows) * rows,
    rows,
    pageSizes: sizes.length > 0 ? sizes : [10, 25, 50, 100],
  }
}

function eligibleTrendDateDimensions(columns: readonly ReportColumn[]): ReportColumn[] | null {
  if (columns.length === 0) return null
  const seen = new Set<string>()
  const result: ReportColumn[] = []
  for (const column of columns) {
    const isDate =
      column.date_col === true || column.sub_type === 'date' || column.sub_type === 'datetime'
    if (
      !column.field ||
      seen.has(column.field) ||
      column.numeric ||
      column.invisible ||
      column.isImage ||
      column.sub_type === 'image' ||
      !isDate
    ) {
      return null
    }
    seen.add(column.field)
    result.push({ ...column })
  }
  return result
}

/** A breakdown has to be a real, non-numeric dimension of the presented set — it doesn't need to
 *  also be a currently visible table column. */
function findBreakdownColumn(
  columns: readonly ReportColumn[],
  field: string,
): ReportColumn | undefined {
  return columns.find((column) => column.field === field && !column.numeric && !column.isImage)
}

function resolveChartTimeBreakdown(
  chartActive: boolean,
  dimensions: readonly string[],
  timeDimensions: readonly string[],
): string | null {
  if (!chartActive) return null
  const time = timeDimensions[0]
  return time && dimensions.some((field) => field !== time) ? time : null
}

function requestUnavailable(
  options: UseDataVisibleReportRequestOptions,
  view: ReportViewType,
  trendUnavailable: boolean,
): boolean {
  if (toValue(options.viewUnavailable)) return true
  // Feature-owned views render through DataVisible slots and own their data lifecycle.
  if (view === 'cohort') return true
  if (view === 'table') return toValue(options.tableUnavailable) || trendUnavailable
  if (view !== 'chart') return false
  return (
    toValue(options.chartCatalogLoading) ||
    toValue(options.chartUnavailable) ||
    !toValue(options.chartFieldsValid)
  )
}

function ensureTrendMetric(metrics: string[], trend: ResolvedDataVisibleTrend | null): void {
  if (trend && !metrics.includes(trend.previewColumn.field)) {
    metrics.push(trend.previewColumn.field)
  }
}

function applyTrendSpec(base: ReportSpec, trend: ResolvedDataVisibleTrend | null): ReportSpec {
  if (!trend) return base
  return {
    ...base,
    ...buildTrendReportSpecPatch({
      breakdownField: trend.breakdownColumn.field,
      previewMetricField: trend.previewColumn.field,
      frequency: trend.frequency,
    }),
  }
}

/** Compose one contract-valid request and opt-in local table modes without leaking UI ids. */
export function useDataVisibleReportRequest(
  options: UseDataVisibleReportRequestOptions,
): UseDataVisibleReportRequest {
  const trend = computed<ResolvedDataVisibleTrend | null>(() => {
    if (toValue(options.tableMode) !== 'trend') return null
    const config = toValue(options.trend)
    const breakdownField = toValue(options.breakdownField)
    if (!config || !breakdownField || !toValue(options.tablePresenterAvailable)) return null
    const columns = toValue(options.columns)
    const breakdownColumn = findBreakdownColumn(columns, breakdownField)
    const previewColumn = columns.find(
      (column) => column.field === config.previewMetric && column.numeric && !column.invisible,
    )
    const dateDimensions = eligibleTrendDateDimensions(config.dateDimensions)
    if (!breakdownColumn || !previewColumn || !dateDimensions) return null
    return { frequency: config.frequency, breakdownColumn, previewColumn, dateDimensions }
  })
  const trendUnavailable = computed(
    () => toValue(options.tableMode) === 'trend' && trend.value === null,
  )
  /**
   * The user's table breakdown dimension, validated against the presented columns. Trend mode owns
   * its own breakdown (a date bucket, via `buildTrendReportSpecPatch`), so the two never apply at
   * once. Null here means the request carries no `breakdown` and the backend returns flat rows.
   */
  const breakdown = computed<string | null>(() => {
    if (toValue(options.tableMode) === 'trend') return null
    if (!toValue(options.tablePresenterAvailable)) return null
    const field = toValue(options.breakdownField)
    if (!field) return null
    return findBreakdownColumn(toValue(options.columns), field) ? field : null
  })

  const coreSpec = computed<ReportSpec | null>(() => {
    const view = toValue(options.view)
    if (requestUnavailable(options, view, trendUnavailable.value)) return null

    const isChart = view === 'chart'
    const activeTrend = view === 'table' ? trend.value : null
    const activeBreakdown = view === 'table' ? breakdown.value : null
    const chartDimensions = [...toValue(options.chartDimensions)]
    // v3 moved the selected time role to `breakdown` whenever a category was also selected. The
    // controller then returns one parent per category with chronological `breakdown_values`, which
    // preserves the top-N category ranking while still giving the chart every date point.
    const chartTimeBreakdown = resolveChartTimeBreakdown(
      toValue(options.chartActive),
      chartDimensions,
      toValue(options.chartTimeDimensions),
    )
    const requestBreakdown = activeBreakdown ?? chartTimeBreakdown
    const chartBreakdownToExclude = activeBreakdown ? null : chartTimeBreakdown
    const dimensions = (
      isChart ? [...toValue(options.chartDimensions)] : [...toValue(options.tableDimensions)]
    ).filter((field) => field !== chartBreakdownToExclude)
    const metrics = isChart
      ? [...toValue(options.chartMetrics)]
      : [...toValue(options.tableMetrics)]
    ensureTrendMetric(metrics, activeTrend)
    const limit = isChart ? toValue(options.chartLimit) : null
    const sort = toValue(options.sort)
    const allFilters = [
      ...toValue(options.filters),
      ...cardStatusFilters(toValue(options.cardStatus) ?? 'all'),
    ]
    const filters = allFilters.filter((filter) => filter.is_change !== true)
    const changeFilters = allFilters.filter((filter) => filter.is_change === true)
    const base: ReportSpec = {
      report_id: toValue(options.reportId),
      dimensions,
      metrics,
      filters: [...filters],
      ...(changeFilters.length ? { change_filters: [...changeFilters] } : {}),
      ...(toValue(options.partitionName)
        ? { partition_name: toValue(options.partitionName) as string }
        : {}),
      ...(requestBreakdown ? { breakdown: requestBreakdown } : {}),
      ...(sort ? { sort_field: sort.field, sort_order: sort.order } : {}),
      ...(toValue(options.defaultPostData)
        ? { defaultPostData: toValue(options.defaultPostData) as string }
        : {}),
      ...(typeof limit === 'number' ? { rows: limit } : {}),
      ...(toValue(options.partitionOnly) ? { partition_only: true } : {}),
      ...(toValue(options.countUniqueDimensions)?.length
        ? { count_unique_dimensions: [...toValue(options.countUniqueDimensions)!] }
        : {}),
      ...(toValue(options.edges)?.length ? { edges: [...toValue(options.edges)!] } : {}),
      ...(toValue(options.targetCurrency)
        ? { target_currency: toValue(options.targetCurrency) as string }
        : {}),
      ...(toValue(options.addCompare) ? { add_compare: true } : {}),
    }
    return applyTrendSpec(base, activeTrend)
  })

  const configuredPage = paginationConfig(toValue(options.pagination))
  const page = ref({
    first: configuredPage?.first ?? 0,
    rows: INITIAL_REQUEST_ROWS,
  })
  let lastCoreKey: string | null = coreSpec.value ? JSON.stringify(coreSpec.value) : null

  watch(
    () => toValue(options.pagination),
    (value) => {
      const next = paginationConfig(value)
      if (next) page.value = { first: next.first, rows: INITIAL_REQUEST_ROWS }
    },
    { deep: true },
  )
  watch(
    () => (coreSpec.value ? JSON.stringify(coreSpec.value) : null),
    (nextKey) => {
      if (nextKey === null) return
      if (lastCoreKey !== null && nextKey !== lastCoreKey) {
        // A new query is a fresh result set — restart the scroll window at its configured size
        // instead of keeping whatever it had grown to for the previous query.
        const configured = paginationConfig(toValue(options.pagination))
        page.value = {
          first: configured?.first ?? 0,
          rows: INITIAL_REQUEST_ROWS,
        }
      }
      lastCoreKey = nextKey
    },
    { flush: 'sync' },
  )

  const pagination = computed<ResolvedDataVisiblePagination | null>(() => {
    // Card pages through the same server offset/limit contract as the table; only chart is unpaged.
    const view = toValue(options.view)
    if ((view !== 'table' && view !== 'card') || coreSpec.value === null) return null
    const config = paginationConfig(toValue(options.pagination))
    if (!config) return null
    return { first: page.value.first, rows: page.value.rows, pageSizes: config.pageSizes }
  })
  const spec = computed<ReportSpec | null>(() => {
    const base = coreSpec.value
    const activePage = pagination.value
    return base && activePage
      ? {
          ...base,
          pagination_enabled: true,
          first: activePage.first,
          rows: activePage.rows,
        }
      : base
  })

  /** Grows the scroll window by one more configured page — the request replays from the same
   *  offset with a bigger `rows`, so the newly settled response is a strict superset the render
   *  pipeline can treat as a normal refetch instead of manually splicing rows together. */
  function loadMore(): void {
    if (!pagination.value) return
    const configured = paginationConfig(toValue(options.pagination))
    const step = positiveInteger(configured?.rows, DEFAULT_PAGE_ROWS)
    page.value = { ...page.value, rows: page.value.rows + step }
  }

  return { spec, trend, trendUnavailable, pagination, loadMore }
}
